import * as styles from "./Background.css";

import { animate } from "animejs";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import type { CSSProperties, JSX, Ref } from "react";

import { TRANSITION_NORMAL_MS } from "@frontend/styles/constants";

import type { BackgroundPatternProps } from "./Background.types";
import { PATTERN_BASE_OPACITY, PATTERN_SPOTLIGHT_OPACITY } from "./constants";
import { buildLattice } from "./lattice";
import type { Lattice } from "./lattice";
import { buildPatternTile } from "./patterns";

const SVG_NS = "http://www.w3.org/2000/svg";

const EMPTY_LATTICE: Lattice = { points: [], shapes: [], shapesByPoint: [] };

const FADE_MS = 300;

const DOT_RADIUS = 1;

const DASH_ARRAY = "2 4";

// Springs the lattice back when the pointer leaves, rather than deflating it
const RELEASE_MS = 900;
const RELEASE_EASE = "outElastic(1, 0.45)";

const ENGAGE_MS = 220;

// Below this a point is considered back at rest and stops being redrawn
const REST_EPSILON = 0.05;

function shapePath(
  lattice: Lattice,
  shape: number[],
  offsetX?: Float32Array,
  offsetY?: Float32Array,
): string {
  let path = "";

  shape.forEach((pointIndex, position) => {
    const point = lattice.points[pointIndex];
    const x = (point?.x ?? 0) + (offsetX?.[pointIndex] ?? 0);
    const y = (point?.y ?? 0) + (offsetY?.[pointIndex] ?? 0);

    path += `${position === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)} `;
  });

  return `${path}Z`;
}

type PatternInkProps = {
  id: string;
  tiled: boolean;
  opacity?: number;
  className?: string;
  style?: CSSProperties;
  ref?: Ref<SVGElement>;
};

function PatternInk({
  id,
  tiled,
  ref,
  ...props
}: PatternInkProps): JSX.Element {
  return tiled ? (
    <rect
      ref={ref as Ref<SVGRectElement>}
      width="100%"
      height="100%"
      fill={`url(#${id})`}
      {...props}
    />
  ) : (
    <use ref={ref as Ref<SVGUseElement>} href={`#${id}`} {...props} />
  );
}

export function BackgroundPattern({
  pattern,
  color,
  cellSize,
  intensity,
  spotlight,
  spotlightSize,
  repel,
  repelRadius,
  repelStrength,
}: BackgroundPatternProps): JSX.Element {
  const latticeId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const spotlightRef = useRef<SVGElement>(null);

  const [size, setSize] = useState({ width: 0, height: 0 });

  // The lattice is measured on the layer itself, in its own pixels: it is the
  // host's box when the background is absolute, the viewport when it is fixed.
  useEffect(() => {
    const layer = svgRef.current;

    if (!layer) {
      return;
    }

    // Rebuilding the lattice means thousands of nodes, so the box is rounded
    // up to whole cells: dragging a window edge only pays for it once a new
    // row or column actually appears.
    const observer = new ResizeObserver(([entry]) => {
      const box = entry?.contentRect;

      if (!box) {
        return;
      }

      const width = Math.ceil(box.width / cellSize) * cellSize;
      const height = Math.ceil(box.height / cellSize) * cellSize;

      setSize((previous) =>
        previous.width === width && previous.height === height
          ? previous
          : { width, height },
      );
    });

    observer.observe(layer);

    return () => observer.disconnect();
  }, [cellSize]);

  // The lattice exists to be deformed. Without `repel` nothing ever moves, so
  // the pattern goes back to a repeating <pattern> tile: two nodes instead of
  // one per dot.
  const lattice = useMemo(
    () =>
      repel
        ? buildLattice(pattern, cellSize, size.width, size.height)
        : EMPTY_LATTICE,
    [repel, pattern, cellSize, size.width, size.height],
  );

  const tile = useMemo(
    () => (repel ? null : buildPatternTile(pattern, cellSize)),
    [repel, pattern, cellSize],
  );

  const isDots = pattern === "dots";

  // The lattice is created imperatively rather than rendered: a few thousand
  // nodes go up in one fragment instead of one reconciliation each, and the
  // pointer effect mutates these very nodes anyway.
  useEffect(() => {
    const group = groupRef.current;

    if (!group || lattice.points.length === 0) {
      return;
    }

    const fragment = document.createDocumentFragment();

    if (isDots) {
      for (const point of lattice.points) {
        const dot = document.createElementNS(SVG_NS, "circle");

        dot.setAttribute("cx", String(point.x));
        dot.setAttribute("cy", String(point.y));
        dot.setAttribute("r", String(DOT_RADIUS));
        fragment.append(dot);
      }
    } else {
      for (const shape of lattice.shapes) {
        const path = document.createElementNS(SVG_NS, "path");

        path.setAttribute("d", shapePath(lattice, shape));
        fragment.append(path);
      }
    }

    group.append(fragment);

    return () => group.replaceChildren();
  }, [lattice, isDots]);

  useEffect(() => {
    const group = groupRef.current;
    const layer = spotlightRef.current;

    // Two levels up: this layer takes no pointer events, so the pointer is
    // tracked on whatever the background was dropped into.
    const host = svgRef.current?.parentElement?.parentElement;

    if (!group || !host || lattice.points.length === 0) {
      return;
    }

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const elements = group.children;

    // Pointer position, and how far the whole effect is engaged (0 at rest)
    const state = { x: -9999, y: -9999, engagement: 0 };

    const offsetX = new Float32Array(lattice.points.length);
    const offsetY = new Float32Array(lattice.points.length);

    const dirtyShapes = new Set<number>();

    function pathFor(shape: number[]): string {
      return shapePath(lattice, shape, offsetX, offsetY);
    }

    function paint() {
      if (!repel) {
        return;
      }

      const radius = repelRadius;
      const push = radius * repelStrength * state.engagement;

      dirtyShapes.clear();

      lattice.points.forEach((point, index) => {
        const dx = point.x - state.x;
        const dy = point.y - state.y;
        const distance = Math.hypot(dx, dy);

        let nextX = 0;
        let nextY = 0;

        if (distance < radius && distance > 0) {
          // Squared falloff: firm right under the pointer, gone at the edge
          const falloff = (1 - distance / radius) ** 2;

          nextX = (dx / distance) * push * falloff;
          nextY = (dy / distance) * push * falloff;
        }

        const previousX = offsetX[index] ?? 0;
        const previousY = offsetY[index] ?? 0;

        if (
          Math.abs(nextX - previousX) < REST_EPSILON &&
          Math.abs(nextY - previousY) < REST_EPSILON
        ) {
          return;
        }

        offsetX[index] = nextX;
        offsetY[index] = nextY;

        if (isDots) {
          const element = elements[index];

          element?.setAttribute("cx", (point.x + nextX).toFixed(2));
          element?.setAttribute("cy", (point.y + nextY).toFixed(2));

          return;
        }

        lattice.shapesByPoint[index]?.forEach((shapeIndex) => {
          dirtyShapes.add(shapeIndex);
        });
      });

      dirtyShapes.forEach((shapeIndex) => {
        const shape = lattice.shapes[shapeIndex];

        if (shape) {
          elements[shapeIndex]?.setAttribute("d", pathFor(shape));
        }
      });
    }

    function engage() {
      animate(state, {
        engagement: 1,
        duration: reducedMotion ? 0 : ENGAGE_MS,
        ease: "outQuad",
        onUpdate: paint,
      });
    }

    function handlePointerMove(event: PointerEvent) {
      const rect = svgRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      // The pointer can already be inside when this subscribes, after a
      // resize or a re-render. No enter event is coming then.
      if (state.engagement === 0) {
        engage();
      }

      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      layer?.style.setProperty("--spotlight-x", `${x}px`);
      layer?.style.setProperty("--spotlight-y", `${y}px`);

      if (reducedMotion) {
        state.x = x;
        state.y = y;
        paint();

        return;
      }

      // Retargets the running tween instead of snapping, so the lattice
      // parts ahead of the pointer rather than sticking to it.
      animate(state, {
        x,
        y,
        duration: TRANSITION_NORMAL_MS,
        ease: "outQuad",
        onUpdate: paint,
      });
    }

    function handlePointerEnter() {
      if (layer) {
        animate(layer, {
          opacity: PATTERN_SPOTLIGHT_OPACITY,
          duration: reducedMotion ? 0 : FADE_MS,
          ease: "outQuad",
        });
      }

      engage();
    }

    function handlePointerLeave() {
      if (layer) {
        animate(layer, {
          opacity: 0,
          duration: reducedMotion ? 0 : FADE_MS,
          ease: "outQuad",
        });
      }

      animate(state, {
        engagement: 0,
        duration: reducedMotion ? 0 : RELEASE_MS,
        ease: reducedMotion ? "linear" : RELEASE_EASE,
        onUpdate: paint,
        onComplete: paint,
      });
    }

    host.addEventListener("pointermove", handlePointerMove, { passive: true });
    host.addEventListener("pointerenter", handlePointerEnter, {
      passive: true,
    });
    host.addEventListener("pointerleave", handlePointerLeave, {
      passive: true,
    });

    return () => {
      host.removeEventListener("pointermove", handlePointerMove);
      host.removeEventListener("pointerenter", handlePointerEnter);
      host.removeEventListener("pointerleave", handlePointerLeave);
    };
  }, [lattice, isDots, repel, repelRadius, repelStrength]);

  return (
    <svg
      ref={svgRef}
      className={styles.patternLayer}
      aria-hidden="true"
      width={size.width}
      height={size.height}
    >
      <defs>
        {tile ? (
          <pattern
            id={`${latticeId}-lattice`}
            width={tile.width}
            height={tile.height}
            patternUnits="userSpaceOnUse"
          >
            {tile.paths.map((d, index) => (
              <path
                key={index}
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={1}
                strokeDasharray={tile.dashed ? DASH_ARRAY : undefined}
              />
            ))}
            {tile.dots.map((dot, index) => (
              <circle
                key={index}
                cx={dot.cx}
                cy={dot.cy}
                r={dot.r}
                fill={color}
              />
            ))}
          </pattern>
        ) : (
          <g
            ref={groupRef}
            id={`${latticeId}-lattice`}
            stroke={isDots ? "none" : color}
            strokeWidth={1}
            strokeDasharray={pattern === "dashed-grid" ? DASH_ARRAY : undefined}
            fill={isDots ? color : "none"}
          />
        )}
      </defs>

      {/* Both layers draw the same geometry, so the repulsion is computed once
          and shows through the spotlight as well. */}
      <PatternInk
        id={`${latticeId}-lattice`}
        tiled={tile !== null}
        opacity={intensity * PATTERN_BASE_OPACITY}
      />

      {spotlight && (
        <PatternInk
          ref={spotlightRef}
          id={`${latticeId}-lattice`}
          tiled={tile !== null}
          className={styles.patternSpotlight}
          style={
            {
              opacity: 0,
              "--spotlight-size": `${spotlightSize}px`,
            } as CSSProperties
          }
        />
      )}
    </svg>
  );
}
